/**
 * Ported VERBATIM from apr_template `src/pages/RoleUtilization.tsx` (Planning & Integration).
 * Only import paths change, plus anything noted inline as a PORT NOTE.
 */
import React, { useState, useEffect } from 'react';
import PageHeader from '../compat/PageHeader';
import { authFetch } from '../compat/api';
import HelpPanel from '../compat/HelpPanel';
import type { Help } from '../compat/helpTypes';
import './ApplicationDependencies.css';
// Decision Workspace design (owner ruling 2026-10-21): kit icons and token pairs.
import { Icon, icons } from '@platform/ui-kit/workspace';
import { WS_CHIP, WS_COLOR, wsTone, wsType } from '../workspace/ws';
import { useWsDialogs } from '../workspace/useWsDialogs';

const RoleUtilizationHelp: Help = {
  title: 'Role Utilization',
  icon: '📅',
  purpose:
    'A weekly, read-only view of how each role and location is loaded across applications &mdash; combining resource allocations with the roadmap schedule to show FTE demand over time.',
  doHere: [
    'Pick a <strong>Start Date</strong> and <strong>Weeks to Show</strong> (9, 26 or 52) to set the time window.',
    'Select at least one filter &mdash; <strong>Integration Group</strong>, <strong>Application</strong>, <strong>Role</strong> or <strong>Location</strong> &mdash; to load data.',
    'Expand a role row (&#9654;) to see the per-application FTE breakdown beneath it.',
    'Read the weekly columns and <strong>Total</strong> to spot peaks; <strong>Download Data</strong> exports the full detail.',
  ],
  fits: 'Integration &rarr; Resource Planning &rarr; Role Utilization',
  firstTime:
    'Choose a start date, select an Integration Group or Role from the filters, then expand a role to see weekly FTE.',
  sections: [
    {
      type: 'html',
      label: 'How FTE is derived',
      html: 'Each role row <strong>sums</strong> the FTE of its applications per week. For a single application, overlapping phases in the same week use the <strong>max</strong> FTE (not the sum).',
    },
  ],
  links: [{ href: '/integration-framework/resource-allocation', label: 'Resource Allocation' }],
};

// PORT NOTE: the source declared its own bearer-token `authFetch` here, duplicating
// `services/api.ts` without its base-URL handling. It is replaced by the module's shared
// shim (import at the top), which adds the `/api/program_planning` prefix and the session
// cookie — without it every call on this screen went to `/api/<x>` and returned 404.

interface ResourceAllocationRecord {
  allocation_id: number;
  application_id_code: string;
  application_name?: string;
  phase_name: string;
  resource_pool_id: number | null;
  allocated_fte: number;
  integration_group_id?: string;
  grouping_id?: string;
  group_name?: string;
}

interface ResourcePool {
  resource_pool_id: number;
  resource_type: string;
  available_capacity_percent: number;
  resource_location: 'On-Site' | 'Off-Shore';
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

interface ScheduleRecord {
  application_id: string;
  application_name: string;
  integration_group_id: string;
  planned_start_date: string;
  planned_end_date: string;
  phase1_start?: string;
  phase1_end?: string;
  phase2_start?: string;
  phase2_end?: string;
  phase3_start?: string;
  phase3_end?: string;
  phase4_start?: string;
  phase4_end?: string;
  phase5_start?: string;
  phase5_end?: string;
}

// Standard phases mapping - maps phase_name to schedule field names
const PHASE_MAPPING: Record<string, { startField: string; endField: string }> = {
  'Assessment_Planning': { startField: 'phase_assessment_start', endField: 'phase_assessment_end' },
  'Design_Architecture': { startField: 'phase_design_start', endField: 'phase_design_end' },
  'Migration_Execution': { startField: 'phase_migration_start', endField: 'phase_migration_end' },
  'Testing_Validation': { startField: 'phase_testing_start', endField: 'phase_testing_end' },
  'GoLive_Optimization': { startField: 'phase_golive_start', endField: 'phase_golive_end' }
};


// Generate week labels for a year
const generateWeeks = (year: number) => {
  const weeks: { weekNumber: number; label: string; dateLabel: string; startDate: Date }[] = [];
  const firstDayOfYear = new Date(year, 0, 1);

  // Find the first Monday
  let firstMonday = new Date(firstDayOfYear);
  const dayOfWeek = firstMonday.getDay();
  if (dayOfWeek !== 1) {
    firstMonday.setDate(firstMonday.getDate() + (dayOfWeek === 0 ? 1 : 8 - dayOfWeek));
  }

  let currentDate = new Date(firstMonday);
  let weekNum = 1;

  while (weekNum <= 52) {
    const startDate = new Date(currentDate);
    const endDate = new Date(currentDate);
    endDate.setDate(endDate.getDate() + 6);

    const startMonth = startDate.toLocaleString('default', { month: 'short' });
    const startDay = startDate.getDate();
    const endMonth = endDate.toLocaleString('default', { month: 'short' });
    const endDay = endDate.getDate();

    // Format: "Jan 6-12" or "Jan 27-Feb 2"
    let dateLabel = '';
    if (startMonth === endMonth) {
      dateLabel = `${startMonth} ${startDay}-${endDay}`;
    } else {
      dateLabel = `${startMonth} ${startDay}-${endMonth} ${endDay}`;
    }

    weeks.push({
      weekNumber: weekNum,
      label: `W${weekNum}`,
      dateLabel,
      startDate
    });

    currentDate.setDate(currentDate.getDate() + 7);
    weekNum++;
  }

  return weeks;
};

// Find the week index that contains a given date (returns -1 if outside range)
const findWeekForDate = (date: Date, weeks: { weekNumber: number; startDate: Date }[]): number => {
  if (weeks.length === 0) return -1;

  // Get end of last week (start + 7 days)
  const lastWeekEnd = new Date(weeks[weeks.length - 1]!.startDate);
  lastWeekEnd.setDate(lastWeekEnd.getDate() + 7);

  // Check if date is before first week or after last week
  if (date < weeks[0]!.startDate || date >= lastWeekEnd) {
    return -1;
  }

  for (let i = weeks.length - 1; i >= 0; i--) {
    if (date >= weeks[i]!.startDate) {
      return i;
    }
  }
  return -1;
};

const RoleUtilization: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [weeks] = useState(() => generateWeeks(currentYear));

  const [records, setRecords] = useState<ResourceAllocationRecord[]>([]);
  const [resourcePools, setResourcePools] = useState<ResourcePool[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [scheduleData, setScheduleData] = useState<ScheduleRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [filterRole, setFilterRole] = useState<string>('');
  const [filterLocation, setFilterLocation] = useState<string>('');
  const [filterIntegrationGroup, setFilterIntegrationGroup] = useState<string>('');
  const [filterApplication, setFilterApplication] = useState<string>('');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [startingWeek, setStartingWeek] = useState<number>(1);
  const [startDate, setStartDate] = useState<string>(() => {
    // Default to today's date
    return new Date().toISOString().split('T')[0]!;
  });
  const [weeksToShow, setWeeksToShow] = useState<number>(9);
  const { notify, dialogs } = useWsDialogs();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [recordsRes, poolsRes, groupsRes, scheduleRes] = await Promise.all([
        authFetch('/api/resource-allocation'),
        authFetch('/api/resource-capacity'),
        authFetch('/api/application-dependencies/lookup/integration-groups?scope=planning'),
        authFetch('/api/roadmap-schedule')
      ]);

      if (recordsRes.ok) {
        const data = await recordsRes.json();
        if (Array.isArray(data)) {
          setRecords(data);
        }
      }

      if (poolsRes.ok) {
        const poolsData = await poolsRes.json();
        setResourcePools(poolsData);
      }

      if (groupsRes.ok) {
        const groupsData = await groupsRes.json();
        if (groupsData.success) {
          setIntegrationGroups(groupsData.data || []);
        }
      }

      if (scheduleRes.ok) {
        const scheduleResult = await scheduleRes.json();
        if (scheduleResult.success && Array.isArray(scheduleResult.data)) {
          setScheduleData(scheduleResult.data);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to load data');
      setLoading(false);
    }
  };

  const getResourcePoolInfo = (poolId: number | null) => {
    if (!poolId) return null;
    return resourcePools.find(p => p.resource_pool_id === poolId) || null;
  };

  // Get unique roles for filter dropdown (without location)
  const uniqueRoles = React.useMemo(() => {
    const roles = new Set<string>();
    resourcePools.forEach(pool => roles.add(pool.resource_type));
    return Array.from(roles).sort();
  }, [resourcePools]);

  // Get unique locations for filter dropdown
  const uniqueLocations = React.useMemo(() => {
    const locations = new Set<string>();
    resourcePools.forEach(pool => locations.add(pool.resource_location));
    return Array.from(locations).sort();
  }, [resourcePools]);

  // Get unique applications for filter dropdown
  const uniqueApplications = React.useMemo(() => {
    const apps: { id: string; name: string }[] = [];
    const seen = new Set<string>();
    records.forEach(record => {
      if (!seen.has(record.application_id_code)) {
        seen.add(record.application_id_code);
        apps.push({
          id: record.application_id_code,
          name: record.application_name || record.application_id_code
        });
      }
    });
    return apps.sort((a, b) => a.id.localeCompare(b.id));
  }, [records]);

  // Generate display weeks based on selected start date
  const displayWeeks = React.useMemo(() => {
    const selectedDate = new Date(startDate);
    const result: { weekNumber: number; label: string; dateLabel: string; startDate: Date }[] = [];

    // Find the Monday of the selected date's week
    const dayOfWeek = selectedDate.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const startMonday = new Date(selectedDate);
    startMonday.setDate(selectedDate.getDate() + mondayOffset);

    let currentDate = new Date(startMonday);

    for (let i = 0; i < weeksToShow; i++) {
      const weekStart = new Date(currentDate);
      const weekEnd = new Date(currentDate);
      weekEnd.setDate(weekEnd.getDate() + 6);

      const startMonth = weekStart.toLocaleString('default', { month: 'short' });
      const startDay = weekStart.getDate();
      const endMonth = weekEnd.toLocaleString('default', { month: 'short' });
      const endDay = weekEnd.getDate();
      const year = weekStart.getFullYear();

      // Get ISO week number
      const tempDate = new Date(weekStart);
      tempDate.setHours(0, 0, 0, 0);
      tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
      const week1 = new Date(tempDate.getFullYear(), 0, 4);
      const weekNum = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);

      let dateLabel = '';
      if (startMonth === endMonth) {
        dateLabel = `${startMonth} ${startDay}-${endDay}`;
      } else {
        dateLabel = `${startMonth} ${startDay}-${endMonth} ${endDay}`;
      }

      result.push({
        weekNumber: weekNum,
        label: `W${weekNum}`,
        dateLabel: `${dateLabel}, ${year}`,
        startDate: weekStart
      });

      currentDate.setDate(currentDate.getDate() + 7);
    }

    return result;
  }, [startDate, weeksToShow]);

  // Application detail type
  interface ApplicationDetail {
    applicationId: string;
    applicationName: string;
    integrationGroupId: string;
    integrationGroupName: string;
    weeklyFte: Record<number, number>;
    totalFte: number;
  }

  // Role summary type with nested applications
  interface RoleSummary {
    role: string;
    location: string;
    applications: ApplicationDetail[];
    weeklyFte: Record<number, number>;  // SUM of all application FTEs
    totalFte: number;
  }

  // Build weekly data - HIERARCHICAL structure: Role summary rows with nested applications
  // Role row: SUM of all application FTEs
  // Application row: Individual FTE using MAX for overlapping phases
  const weeklyData = React.useMemo(() => {
    // No data shown until user makes a filter selection (empty string means "Select...")
    // "__ALL__" means show all, specific value means filter by that value
    const hasIntegrationGroupSelection = filterIntegrationGroup !== '';
    const hasApplicationSelection = filterApplication !== '';
    const hasRoleSelection = filterRole !== '';
    const hasLocationSelection = filterLocation !== '';

    if (!hasRoleSelection && !hasLocationSelection && !hasIntegrationGroupSelection && !hasApplicationSelection) {
      return [];
    }

    // First, build application-level data
    // Key: role|location|applicationId
    const appRows: Record<string, {
      role: string;
      location: string;
      applicationId: string;
      applicationName: string;
      integrationGroupId: string;
      integrationGroupName: string;
      weeklyFte: Record<number, number>;
      totalFte: number;
    }> = {};

    // Process each allocation record
    records.forEach(record => {
      const pool = getResourcePoolInfo(record.resource_pool_id);
      if (!pool) return;

      // Apply filters (skip if "__ALL__" which means show all)
      if (filterRole && filterRole !== '__ALL__' && pool.resource_type !== filterRole) return;
      if (filterLocation && filterLocation !== '__ALL__' && pool.resource_location !== filterLocation) return;

      // Filter by integration group
      const integrationGroupId = record.integration_group_id || record.grouping_id || '';
      if (filterIntegrationGroup && filterIntegrationGroup !== '__ALL__' && integrationGroupId !== filterIntegrationGroup) return;

      // Filter by application
      if (filterApplication && filterApplication !== '__ALL__' && record.application_id_code !== filterApplication) return;

      // Get integration group name
      const groupInfo = integrationGroups.find(g => g.integration_group_id === integrationGroupId);
      const integrationGroupName = groupInfo?.integration_group_description || integrationGroupId || 'Ungrouped';

      // Unique key for each Role + Location + Application combination
      const appKey = `${pool.resource_type}|${pool.resource_location}|${record.application_id_code}`;

      if (!appRows[appKey]) {
        appRows[appKey] = {
          role: pool.resource_type,
          location: pool.resource_location,
          applicationId: record.application_id_code,
          applicationName: record.application_name || record.application_id_code,
          integrationGroupId: integrationGroupId,
          integrationGroupName: integrationGroupName,
          weeklyFte: {},
          totalFte: 0
        };
      }
      const appData = appRows[appKey];

      // Find schedule for this application - prioritize exact application_id match
      // First try to find by application_id, then fall back to integration_group_id
      let schedule = scheduleData.find(s => s.application_id === record.application_id_code);
      if (!schedule) {
        schedule = scheduleData.find(s => s.integration_group_id === integrationGroupId);
      }

      if (schedule) {
        // Get phase dates
        const phaseMapping = PHASE_MAPPING[record.phase_name];
        if (phaseMapping) {
          const startDateStr = (schedule as any)[phaseMapping.startField];
          const endDateStr = (schedule as any)[phaseMapping.endField];

          if (startDateStr && endDateStr) {
            // Parse UTC date string and extract date components to avoid timezone issues
            // The API returns dates like "2026-02-17T05:00:00.000Z" which represents midnight local time
            const parseUTCDateAsLocal = (dateStr: string): Date => {
              // Extract just the date part (YYYY-MM-DD) from the ISO string
              const datePart = dateStr.split('T')[0]!;
              const [year, month, day] = datePart.split('-').map(Number);
              return new Date(year!, month! - 1, day);
            };

            const phaseStartDate = parseUTCDateAsLocal(startDateStr);
            const phaseEndDate = parseUTCDateAsLocal(endDateStr);

            // FTE is allocated per week during the phase
            const ftePerWeek = parseFloat(String(record.allocated_fte || 0));

            // Find which displayed weeks overlap with this phase
            displayWeeks.forEach((week) => {
              const weekStart = new Date(week.startDate.getFullYear(), week.startDate.getMonth(), week.startDate.getDate());
              const weekEnd = new Date(weekStart);
              weekEnd.setDate(weekEnd.getDate() + 6);

              // Check if this week overlaps with the phase dates
              if (phaseStartDate <= weekEnd && phaseEndDate >= weekStart) {
                // For same application, use MAX FTE when phases overlap in same week
                appData.weeklyFte[week.weekNumber] = Math.max(appData.weeklyFte[week.weekNumber] || 0, ftePerWeek);
              }
            });
          }
        }
      }
    });

    // Calculate totalFte for each application from its weekly values
    Object.values(appRows).forEach(app => {
      app.totalFte = Object.values(app.weeklyFte).reduce((sum, fte) => sum + fte, 0);
    });

    // Now group applications by Role|Location and create summary rows
    const roleSummaries: Record<string, RoleSummary> = {};

    Object.values(appRows).forEach(app => {
      const roleKey = `${app.role}|${app.location}`;

      if (!roleSummaries[roleKey]) {
        roleSummaries[roleKey] = {
          role: app.role,
          location: app.location,
          applications: [],
          weeklyFte: {},
          totalFte: 0
        };
      }

      const summary = roleSummaries[roleKey];
      summary.applications.push({
        applicationId: app.applicationId,
        applicationName: app.applicationName,
        integrationGroupId: app.integrationGroupId,
        integrationGroupName: app.integrationGroupName,
        weeklyFte: app.weeklyFte,
        totalFte: app.totalFte
      });

      // SUM application FTEs into role summary
      Object.entries(app.weeklyFte).forEach(([week, fte]) => {
        const weekNum = parseInt(week);
        summary.weeklyFte[weekNum] = (summary.weeklyFte[weekNum] || 0) + fte;
      });
    });

    // Calculate total FTE for each role summary
    Object.values(roleSummaries).forEach(summary => {
      summary.totalFte = Object.values(summary.weeklyFte).reduce((sum, fte) => sum + fte, 0);
      // Sort applications by integration group, then by name
      summary.applications.sort((a, b) => {
        const groupCompare = a.integrationGroupName.localeCompare(b.integrationGroupName);
        if (groupCompare !== 0) return groupCompare;
        return a.applicationName.localeCompare(b.applicationName);
      });
    });

    // Convert to array and sort by Role, Location
    return Object.values(roleSummaries).sort((a, b) => {
      const roleCompare = a.role.localeCompare(b.role);
      if (roleCompare !== 0) return roleCompare;
      return a.location.localeCompare(b.location);
    });
  }, [records, resourcePools, scheduleData, displayWeeks, filterRole, filterLocation, filterIntegrationGroup, filterApplication, integrationGroups]);

  // Calculate weekly totals from role summaries
  const weeklyTotals = React.useMemo(() => {
    const totals: Record<number, number> = {};
    weeklyData.forEach(roleSummary => {
      Object.entries(roleSummary.weeklyFte).forEach(([week, fte]) => {
        const weekNum = parseInt(week);
        totals[weekNum] = (totals[weekNum] || 0) + fte;
      });
    });
    return totals;
  }, [weeklyData]);

  const grandTotal = weeklyData.reduce((sum, roleSummary) => sum + (roleSummary.totalFte || 0), 0);

  // Toggle row expansion
  const toggleRowExpansion = (roleKey: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(roleKey)) {
        newSet.delete(roleKey);
      } else {
        newSet.add(roleKey);
      }
      return newSet;
    });
  };

  // All rows collapsed by default - no auto-expand

  const downloadData = () => {
    // Check if any filter is selected
    const hasIntegrationGroupSelection = filterIntegrationGroup !== '';
    const hasApplicationSelection = filterApplication !== '';
    const hasRoleSelection = filterRole !== '';
    const hasLocationSelection = filterLocation !== '';

    if (!hasRoleSelection && !hasLocationSelection && !hasIntegrationGroupSelection && !hasApplicationSelection) {
      notify('Please select at least one filter to download data', 'info');
      return;
    }

    // Helper to parse UTC date string as local date
    const parseUTCDateAsLocal = (dateStr: string): Date => {
      const datePart = dateStr.split('T')[0]!;
      const [year, month, day] = datePart.split('-').map(Number);
      return new Date(year!, month! - 1, day);
    };

    // Helper to get ISO week number for a date
    const getWeekNumber = (date: Date): { weekNum: number; year: number } => {
      const tempDate = new Date(date.getTime());
      tempDate.setHours(0, 0, 0, 0);
      tempDate.setDate(tempDate.getDate() + 3 - (tempDate.getDay() + 6) % 7);
      const week1 = new Date(tempDate.getFullYear(), 0, 4);
      const weekNum = 1 + Math.round(((tempDate.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
      return { weekNum, year: tempDate.getFullYear() };
    };

    // Helper to get Monday of a given ISO week
    const getWeekStart = (year: number, weekNum: number): Date => {
      const jan4 = new Date(year, 0, 4);
      const dayOfWeek = jan4.getDay() || 7;
      const firstMonday = new Date(jan4);
      firstMonday.setDate(jan4.getDate() - dayOfWeek + 1);
      const weekStart = new Date(firstMonday);
      weekStart.setDate(firstMonday.getDate() + (weekNum - 1) * 7);
      return weekStart;
    };

    // Build application-level data with ALL weeks
    const appRows: Record<string, {
      role: string;
      location: string;
      applicationId: string;
      applicationName: string;
      integrationGroupId: string;
      integrationGroupName: string;
      weeklyFte: Record<string, number>; // key: "year-weekNum"
      totalFte: number;
    }> = {};

    // Track all unique year-week combinations
    const allYearWeeks = new Set<string>();

    // Process each allocation record
    records.forEach(record => {
      const pool = getResourcePoolInfo(record.resource_pool_id);
      if (!pool) return;

      // Apply filters
      if (filterRole && filterRole !== '__ALL__' && pool.resource_type !== filterRole) return;
      if (filterLocation && filterLocation !== '__ALL__' && pool.resource_location !== filterLocation) return;

      const integrationGroupId = record.integration_group_id || record.grouping_id || '';
      if (filterIntegrationGroup && filterIntegrationGroup !== '__ALL__' && integrationGroupId !== filterIntegrationGroup) return;
      if (filterApplication && filterApplication !== '__ALL__' && record.application_id_code !== filterApplication) return;

      const groupInfo = integrationGroups.find(g => g.integration_group_id === integrationGroupId);
      const integrationGroupName = groupInfo?.integration_group_description || integrationGroupId || 'Ungrouped';

      const appKey = `${pool.resource_type}|${pool.resource_location}|${record.application_id_code}`;

      if (!appRows[appKey]) {
        appRows[appKey] = {
          role: pool.resource_type,
          location: pool.resource_location,
          applicationId: record.application_id_code,
          applicationName: record.application_name || record.application_id_code,
          integrationGroupId: integrationGroupId,
          integrationGroupName: integrationGroupName,
          weeklyFte: {},
          totalFte: 0
        };
      }
      const appData = appRows[appKey];

      // Find schedule for this application
      let schedule = scheduleData.find(s => s.application_id === record.application_id_code);
      if (!schedule) {
        schedule = scheduleData.find(s => s.integration_group_id === integrationGroupId);
      }

      if (schedule) {
        const phaseMapping = PHASE_MAPPING[record.phase_name];
        if (phaseMapping) {
          const startDateStr = (schedule as any)[phaseMapping.startField];
          const endDateStr = (schedule as any)[phaseMapping.endField];

          if (startDateStr && endDateStr) {
            const phaseStartDate = parseUTCDateAsLocal(startDateStr);
            const phaseEndDate = parseUTCDateAsLocal(endDateStr);
            const ftePerWeek = parseFloat(String(record.allocated_fte || 0));

            // Iterate through ALL weeks in the phase date range
            let currentDate = new Date(phaseStartDate);
            while (currentDate <= phaseEndDate) {
              const { weekNum, year } = getWeekNumber(currentDate);
              const yearWeekKey = `${year}-${weekNum.toString().padStart(2, '0')}`;

              allYearWeeks.add(yearWeekKey);

              // Use MAX FTE when phases overlap in same week
              appData.weeklyFte[yearWeekKey] = Math.max(appData.weeklyFte[yearWeekKey] || 0, ftePerWeek);

              // Move to next week
              currentDate.setDate(currentDate.getDate() + 7);
            }
          }
        }
      }
    });

    // Calculate totalFte for each application
    Object.values(appRows).forEach(app => {
      app.totalFte = Object.values(app.weeklyFte).reduce((sum, fte) => sum + fte, 0);
    });

    // Group by Role|Location
    const roleSummaries: Record<string, {
      role: string;
      location: string;
      applications: typeof appRows[string][];
      weeklyFte: Record<string, number>;
      totalFte: number;
    }> = {};

    Object.values(appRows).forEach(app => {
      const roleKey = `${app.role}|${app.location}`;

      if (!roleSummaries[roleKey]) {
        roleSummaries[roleKey] = {
          role: app.role,
          location: app.location,
          applications: [],
          weeklyFte: {},
          totalFte: 0
        };
      }

      const summary = roleSummaries[roleKey];
      summary.applications.push(app);

      Object.entries(app.weeklyFte).forEach(([yearWeek, fte]) => {
        summary.weeklyFte[yearWeek] = (summary.weeklyFte[yearWeek] || 0) + fte;
      });
    });

    Object.values(roleSummaries).forEach(summary => {
      summary.totalFte = Object.values(summary.weeklyFte).reduce((sum, fte) => sum + fte, 0);
      summary.applications.sort((a, b) => {
        const groupCompare = a.integrationGroupName.localeCompare(b.integrationGroupName);
        if (groupCompare !== 0) return groupCompare;
        return a.applicationName.localeCompare(b.applicationName);
      });
    });

    const sortedRoleSummaries = Object.values(roleSummaries).sort((a, b) => {
      const roleCompare = a.role.localeCompare(b.role);
      if (roleCompare !== 0) return roleCompare;
      return a.location.localeCompare(b.location);
    });

    if (sortedRoleSummaries.length === 0) {
      notify('No data to download for the selected filters', 'info');
      return;
    }

    // Sort year-week keys chronologically
    const sortedYearWeeks = Array.from(allYearWeeks).sort();

    // Generate week labels and date ranges
    const weekLabels = sortedYearWeeks.map(yw => {
      const [year, week] = yw.split('-').map(Number);
      return `W${week}`;
    });

    const weekDateLabels = sortedYearWeeks.map(yw => {
      const [year, week] = yw.split('-').map(Number);
      const weekStart = getWeekStart(year!, week!);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startMonth = weekStart.toLocaleString('default', { month: 'short' });
      const startDay = weekStart.getDate();
      const endMonth = weekEnd.toLocaleString('default', { month: 'short' });
      const endDay = weekEnd.getDate();
      const weekYear = weekStart.getFullYear();

      if (startMonth === endMonth) {
        return `${startMonth} ${startDay}-${endDay}, ${weekYear}`;
      } else {
        return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${weekYear}`;
      }
    });

    const csvRows: string[] = [];

    // Row 1: Column labels + Week numbers
    const headerRow1 = ['Role', 'Location', 'Integration Group', 'Application ID', 'Application Name', ...weekLabels, 'Total FTE'];
    csvRows.push(headerRow1.join(','));

    // Row 2: Empty cells for fixed columns + Date ranges with year
    const headerRow2 = ['', '', '', '', '', ...weekDateLabels.map(d => `"${d}"`), ''];
    csvRows.push(headerRow2.join(','));

    // Calculate totals for all weeks
    const allWeekTotals: Record<string, number> = {};
    sortedYearWeeks.forEach(yw => {
      allWeekTotals[yw] = sortedRoleSummaries.reduce((sum, rs) => sum + (rs.weeklyFte[yw] || 0), 0);
    });

    let grandTotalFte = 0;

    sortedRoleSummaries.forEach(roleSummary => {
      grandTotalFte += roleSummary.totalFte;

      // Add role summary row
      const summaryRow = [
        `"${roleSummary.role.replace(/"/g, '""')}"`,
        `"${roleSummary.location.replace(/"/g, '""')}"`,
        '""',
        '""',
        '"(Summary)"',
        ...sortedYearWeeks.map(yw => Number(roleSummary.weeklyFte[yw] || 0).toFixed(2)),
        Number(roleSummary.totalFte || 0).toFixed(2)
      ];
      csvRows.push(summaryRow.join(','));

      // Add application detail rows
      roleSummary.applications.forEach(app => {
        const appRow = [
          `"${roleSummary.role.replace(/"/g, '""')}"`,
          `"${roleSummary.location.replace(/"/g, '""')}"`,
          `"${app.integrationGroupName.replace(/"/g, '""')}"`,
          `"${app.applicationId.replace(/"/g, '""')}"`,
          `"${app.applicationName.replace(/"/g, '""')}"`,
          ...sortedYearWeeks.map(yw => Number(app.weeklyFte[yw] || 0).toFixed(2)),
          Number(app.totalFte || 0).toFixed(2)
        ];
        csvRows.push(appRow.join(','));
      });
    });

    // Add totals row
    const totalsRow = [
      '"TOTAL"',
      '""',
      '""',
      '""',
      '""',
      ...sortedYearWeeks.map(yw => Number(allWeekTotals[yw] || 0).toFixed(2)),
      Number(grandTotalFte || 0).toFixed(2)
    ];
    csvRows.push(totalsRow.join(','));

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `role_utilization_detailed_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  // DW: shared cell shapes for the weekly grid. Widths, padding and sticky offsets are the
  // source's layout values; type and colour come from the kit tokens.
  const fixedCol = (px: number): React.CSSProperties => ({ width: `${px}px`, minWidth: `${px}px`, maxWidth: `${px}px` });
  const gridBorder = `1px solid ${WS_COLOR.border}`;
  const labelStyle: React.CSSProperties = { ...wsType('label') };
  const emptyDash: React.CSSProperties = { color: WS_COLOR.textMuted };

  return (
    <div className="page" style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <HelpPanel help={RoleUtilizationHelp} />
      {/* Header */}
      <PageHeader title={<>Role Utilization</>} subtitle={<>Weekly resource allocation view by role and location</>} />
      <div className="page-header__actions" style={{ display: 'flex', gap: '12px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '16px' }}>
{weeklyData.length > 0 && (
              <button
                className="btn"
                onClick={downloadData}
                title="Download current view as CSV"
              >
                <Icon icon={icons.Download} size={20} /> Download Data
              </button>
            )}
      </div>

      {/* Scrollable Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
        {loading && (
          <div className="md-loading" style={{ padding: '40px', textAlign: 'center' }}>
            Loading role utilization data...
          </div>
        )}

        {!loading && (
          <>
            {/* Filters */}
            <div className="md-card" style={{ marginBottom: '16px', padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <label style={labelStyle}>Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ minWidth: '150px', cursor: 'pointer' }}
                />

                <label style={labelStyle}>Weeks to Show:</label>
                <select
                  value={weeksToShow}
                  onChange={(e) => setWeeksToShow(Number(e.target.value))}
                  style={{ minWidth: '80px' }}
                >
                  <option value={9}>9</option>
                  <option value={26}>26</option>
                  <option value={52}>52</option>
                </select>

                <label style={labelStyle}>Integration Group:</label>
                <select
                  value={filterIntegrationGroup}
                  onChange={(e) => setFilterIntegrationGroup(e.target.value)}
                  style={{ minWidth: '280px' }}
                >
                  <option value="">-- Select Grouping ID --</option>
                  <option value="__ALL__">All Groups</option>
                  {integrationGroups.map(group => (
                    <option key={group.integration_group_id} value={group.integration_group_id}>
                      {group.integration_group_id} - {group.integration_group_description || 'No Description'}
                    </option>
                  ))}
                </select>

                <label style={labelStyle}>Application:</label>
                <select
                  value={filterApplication}
                  onChange={(e) => setFilterApplication(e.target.value)}
                  style={{ minWidth: '280px' }}
                >
                  <option value="">-- Select Application --</option>
                  <option value="__ALL__">All Applications</option>
                  {uniqueApplications.map(app => (
                    <option key={app.id} value={app.id}>
                      {app.id} - {app.name}
                    </option>
                  ))}
                </select>

                {(filterRole || filterLocation || filterIntegrationGroup || filterApplication) && (
                  <button
                    className="btn"
                    onClick={() => {
                      setFilterRole('');
                      setFilterLocation('');
                      setFilterIntegrationGroup('');
                      setFilterApplication('');
                    }}
                  >
                    Clear Filters
                  </button>
                )}
              </div>

              {/* Second row - Role and Location filters */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginTop: '12px' }}>
                <label style={labelStyle}>Role:</label>
                <select
                  value={filterRole}
                  onChange={(e) => setFilterRole(e.target.value)}
                  style={{ minWidth: '200px' }}
                >
                  <option value="">-- Select Role --</option>
                  <option value="__ALL__">All Roles</option>
                  {uniqueRoles.map(role => (
                    <option key={role} value={role}>
                      {role}
                    </option>
                  ))}
                </select>

                <label style={{ ...labelStyle, marginLeft: '16px' }}>Location:</label>
                <select
                  value={filterLocation}
                  onChange={(e) => setFilterLocation(e.target.value)}
                  style={{ minWidth: '150px' }}
                >
                  <option value="">-- Select Location --</option>
                  <option value="__ALL__">All Locations</option>
                  {uniqueLocations.map(location => (
                    <option key={location} value={location}>
                      {location}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Weekly Data Table */}
            <div className="md-card" style={{ overflow: 'hidden' }}>
              {weeklyData.length === 0 ? (
                <div className="md-empty-state" style={{ padding: '40px' }}>
                  <div className="md-empty-icon"><Icon icon={icons.Users} size={24} /></div>
                  <h3>{(!filterRole && !filterLocation && !filterIntegrationGroup && !filterApplication)
                    ? 'Select a Filter to View Data'
                    : 'No Role Utilization Data Found'}</h3>
                  <p>{(!filterRole && !filterLocation && !filterIntegrationGroup && !filterApplication)
                    ? 'Please select a Role, Location, Integration Group, or Application from the filters above to view utilization data.'
                    : 'No data matches your current filter selection.'}</p>
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table className="md-table" style={{
                    borderCollapse: 'collapse',
                    minWidth: `${320 + (displayWeeks.length * 100) + 100}px`
                  }}>
                    <thead>
                      <tr>
                        <th style={{
                          padding: '14px 12px',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          borderRight: gridBorder,
                          position: 'sticky',
                          left: 0,
                          backgroundColor: WS_COLOR.surfaceSubtle,
                          zIndex: 2,
                          ...fixedCol(320)
                        }}>
                          Role / Application
                        </th>
                        {displayWeeks.map(week => (
                          <th key={week.weekNumber} style={{
                            padding: '10px 6px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                            borderRight: gridBorder,
                            ...fixedCol(100)
                          }}>
                            <div style={{ ...wsType('tableHeader'), color: WS_COLOR.primary }}>{week.label}</div>
                            <div style={{ ...wsType('meta'), color: WS_COLOR.textMuted, marginTop: '2px' }}>{week.dateLabel}</div>
                          </th>
                        ))}
                        <th style={{
                          padding: '14px 12px',
                          textAlign: 'center',
                          ...wsTone('info'),
                          whiteSpace: 'nowrap',
                          ...fixedCol(100)
                        }}>
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {weeklyData.map((roleSummary, roleIndex) => {
                        const roleKey = `${roleSummary.role}|${roleSummary.location}`;
                        const isExpanded = expandedRows.has(roleKey);
                        const rows: React.ReactNode[] = [];

                        // Role summary row (expandable)
                        rows.push(
                          <tr
                            key={roleKey}
                            style={{
                              backgroundColor: WS_COLOR.surfaceSubtle,
                              cursor: 'pointer'
                            }}
                            onClick={() => toggleRowExpansion(roleKey)}
                          >
                            <td style={{
                              padding: '10px 12px',
                              fontWeight: 600,
                              borderBottom: gridBorder,
                              position: 'sticky',
                              left: 0,
                              backgroundColor: WS_COLOR.surfaceSubtle,
                              zIndex: 1,
                              ...fixedCol(320)
                            }}>
                              <span style={{ marginRight: '8px', display: 'inline-flex', verticalAlign: 'middle' }}>
                                <Icon icon={isExpanded ? icons.ChevronDown : icons.ChevronRight} size={16} />
                              </span>
                              {roleSummary.role}
                              {/* DW: location chip — info pair for On-Site, success pair for Off-Shore (was blue/green text). */}
                              <span style={{ ...WS_CHIP, ...wsTone(roleSummary.location === 'On-Site' ? 'info' : 'success'), marginLeft: '8px' }}>
                                {roleSummary.location}
                              </span>
                              <span style={{ marginLeft: '8px', ...wsType('meta'), color: WS_COLOR.textMuted }}>
                                - {roleSummary.applications.length} apps
                              </span>
                            </td>
                            {displayWeeks.map(week => {
                              const weekFte = roleSummary.weeklyFte[week.weekNumber] || 0;
                              return (
                                <td key={week.weekNumber} style={{
                                  padding: '10px 6px',
                                  textAlign: 'center',
                                  borderBottom: gridBorder,
                                  backgroundColor: WS_COLOR.surfaceSubtle,
                                  ...fixedCol(100)
                                }}>
                                  {weekFte > 0 ? (
                                    <span style={{ fontWeight: 600, color: WS_COLOR.text }}>
                                      {Number(weekFte).toFixed(2)}
                                    </span>
                                  ) : (
                                    <span style={emptyDash}>-</span>
                                  )}
                                </td>
                              );
                            })}
                            <td style={{
                              padding: '10px 12px',
                              textAlign: 'center',
                              fontWeight: 600,
                              ...wsTone('info'),
                              borderBottom: gridBorder,
                              ...fixedCol(100)
                            }}>
                              {Number(roleSummary.totalFte || 0).toFixed(2)}
                            </td>
                          </tr>
                        );

                        // Application detail rows (shown when expanded)
                        if (isExpanded) {
                          roleSummary.applications.forEach((app, appIndex) => {
                            const rowBg = appIndex % 2 === 0 ? WS_COLOR.surface : WS_COLOR.background;
                            rows.push(
                              <tr
                                key={`${roleKey}|${app.applicationId}`}
                                style={{
                                  backgroundColor: rowBg
                                }}
                              >
                                <td style={{
                                  padding: '8px 12px 8px 32px',
                                  borderBottom: gridBorder,
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: rowBg,
                                  zIndex: 1,
                                  ...wsType('meta'),
                                  ...fixedCol(320),
                                  color: WS_COLOR.text
                                }}>
                                  {app.applicationId} - {app.applicationName}
                                </td>
                                {displayWeeks.map(week => {
                                  const weekFte = app.weeklyFte[week.weekNumber] || 0;
                                  return (
                                    <td key={week.weekNumber} style={{
                                      padding: '8px 6px',
                                      textAlign: 'center',
                                      borderBottom: gridBorder,
                                      backgroundColor: 'transparent',
                                      ...fixedCol(100)
                                    }}>
                                      {weekFte > 0 ? (
                                        <span style={{ ...wsType('meta'), fontWeight: 500, color: WS_COLOR.text }}>
                                          {Number(weekFte).toFixed(2)}
                                        </span>
                                      ) : (
                                        <span style={emptyDash}>-</span>
                                      )}
                                    </td>
                                  );
                                })}
                                <td style={{
                                  padding: '8px 12px',
                                  textAlign: 'center',
                                  ...wsType('meta'),
                                  fontWeight: 500,
                                  ...wsTone('info'),
                                  borderBottom: gridBorder,
                                  ...fixedCol(100)
                                }}>
                                  {Number(app.totalFte || 0).toFixed(2)}
                                </td>
                              </tr>
                            );
                          });
                        }

                        return rows;
                      })}
                    </tbody>
                    {/* Totals Footer */}
                    <tfoot>
                      <tr style={{ backgroundColor: WS_COLOR.surface, borderTop: `2px solid ${WS_COLOR.border}` }}>
                        <td style={{
                          padding: '12px',
                          fontWeight: 600,
                          color: WS_COLOR.text,
                          position: 'sticky',
                          left: 0,
                          backgroundColor: WS_COLOR.surface,
                          zIndex: 1,
                          ...fixedCol(320)
                        }}>
                          TOTAL
                        </td>
                        {displayWeeks.map(week => {
                          const weekTotal = weeklyTotals[week.weekNumber] || 0;
                          return (
                            <td key={week.weekNumber} style={{
                              padding: '12px 6px',
                              textAlign: 'center',
                              fontWeight: 600,
                              color: WS_COLOR.text,
                              ...fixedCol(100)
                            }}>
                              {Number(weekTotal).toFixed(2)}
                            </td>
                          );
                        })}
                        <td style={{
                          padding: '12px',
                          textAlign: 'center',
                          ...wsType('body'),
                          fontWeight: 600,
                          ...wsTone('info'),
                          ...fixedCol(100)
                        }}>
                          {Number(grandTotal || 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* Error Messages */}
      {error && (
        <div className="md-toast md-toast--error">
          {error}
          <button onClick={() => setError('')} aria-label="Dismiss"><Icon icon={icons.X} size={16} /></button>
        </div>
      )}
      {dialogs}
    </div>
  );
};

export default RoleUtilization;
